import {observer} from 'mobx-react-lite';
import debounce from 'lodash/debounce';
import {useState, useEffect, useRef} from 'react';
import {getLocalesByKey as t} from '@/utils/translation';
import {
  Tooltip,
  Form,
  Typography,
  Dropdown,
  Checkbox,
  Modal,
  Spin,
  Divider,
  Select,
} from 'antd';
import styles from './index.module.scss';
import {NextImg} from '@/utils/nextImg';

import {useStore} from '@/store/root-store';
import isURL from 'validator/lib/isURL';
import {Button, CommonInput, Modal as CommonModal} from '@/components/common-components/components';
import {SCA_PAGE_URL_UPDATE} from '@/constants/events';

import {MixPanel} from '@/utils/mixpanel';
import {useRouter} from 'next/router';
import styled from 'styled-components';
import {faPen} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  faArrowDownToBracket,
  faArrowUpFromBracket,
  faXmark,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons';
import {MetaTagGenerator} from './MetaGenerator/MetaTagGenerator';

import {WpInstructionsModal} from './OneClickPublisher/WpInstructionsModal';
import {WpAddSiteModal} from './OneClickPublisher/WpAddSiteModal';
import {OneClickPublisherV2} from './OneClickPublisherV2/OneClickPublisherV2';
import {Button as V2Button, GrayButton} from '@/components/common-components/v2';
import {getDomain} from '@/utils/url';
import {LoadingOutlined} from '@ant-design/icons';
import {isNil} from 'lodash';
import {isScaPublicPage} from '../../EditorSection/content-editor/utils';
import {Loader} from '../../../RightSidebar/v2-sidebar/Utils/loader';
import {ScaArticleStatusSelectorV2} from '../../../../blocks/status-selector-v2';
import {ProjectsSelector} from '../../../../blocks/projects-selector';
import {languages} from '../../../Drawers/AiContentToolsDrawer/languages';
import Flag from '@/components/common-components/components/flag';
import {formatCountryCode} from '@/components/dashboard/pages/content-ideas/drawer/languageUtil';
import {notification} from '@/utils/notification';

interface TitleEditorProps {
  placeholder?: string;
}

export const TitleEditor = observer(
  ({placeholder = 'New document'}: TitleEditorProps) => {
    const {contentOptimizer: {
      currentPage: {
        content,
        isPublic,
        contentLoadedAt,
        isAnalyzing,
        editTitle,
        uploadFromUrl,
        isExportingPage,
        isExternalTitleEdit,
        viewers,
        smallPageLoad,
      },
      wpPublish: {
        setShowQuickSyncModal,
        showQuickSyncModal,
        publishWpPage,
        wpArray,
        fetchWpPublishData,
        postingData,
      },
      aiMetaData: {
        metaData,
      },
      aiOutline: {
        userAiSettings,
        updateUserAiSettings,
        getUserAiSettings,
      },
    }} = useStore('');
    const [titleLeft, setTitleLeft] = useState(0);
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState(content?.importedFromUrl || '');
    const [modalUrl, setModalUrl] = useState(content?.importedFromUrl || '');
    const [editPageUrl, setEditPageUrl] = useState(false);
    const [isCloseClicked, setIsCloseClicked] = useState(false);
    const [showDownloadConfirmationModal, setShowDownloadConfirmationModal] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [renderWithbrowser, setRenderWithBrowser] = useState(false);
    const [keepContentModal, setKeepContentModal] = useState({
      show: false,
      url: '',
      renderWithbrowser: false,
    });
    const [titleContainerWidth, setTitleContainerWidth] = useState('100%');

    useEffect(() => {
      if (!title || content?.title === title) {
        setTitle(content?.title || '');
      }
    }, [content?.title]);

    useEffect(()=>{
      getUserAiSettings();
    }, []);

    useEffect(()=>{
      if (metaData?.length && content?.title?.startsWith('New Document')) {
        setTitle(metaData[0]?.metaTitle);
      }
    }, [content, content?.title, metaData]);

    useEffect(() => {
      calculateWidth();
    }, [title]);

    const antIcon = <LoadingOutlined style={{fontSize: 16, marginLeft: 5}} spin />;


    const [collapsedViewers, setCollapsedViewers] = useState(false);

    const [form] = Form.useForm();


    const {Text} = Typography;
    const titleRef = useRef(null);

    // this will take in left position of the title elem so we can align the people currently editing banner to it
    useEffect(() => {
      const getTitleLeft = () => {
        if (titleRef?.current?.getBoundingClientRect()?.left) setTitleLeft(titleRef?.current?.getBoundingClientRect()?.left);
      };

      window.addEventListener('resize', getTitleLeft);

      getTitleLeft();

      return () => window.removeEventListener('resize', getTitleLeft);
    }, [titleRef?.current]);


    const {
      query: {newPage},
    } = useRouter();


    useEffect(() => {
      if (!isScaPublicPage()) {
        fetchWpPublishData();
      }
      setTitle(content?.title || '');
      setUrl(content?.importedFromUrl || '');
      form.setFieldsValue({
        urlInput: url,
      });
    }, [contentLoadedAt]);

    useEffect(() => {
      if (isExternalTitleEdit) setTitle(content?.title || '');
    }, [isExternalTitleEdit]);


    const debouncedChange = debounce(e => {
      if (!isNil(e.target.value)) editTitle(e.target.value);
    }, 2000);

    const debouncedChangeFocusOut = debounce(e => {
      if (!isNil(e.target.value)) editTitle(e.target.value);
    }, 0);

    const onChange = e => {
      setTitle(e.target.value);
    };

    const URL_PATTERN = new RegExp('http(s?)://');
    const popupRef = useRef<HTMLInputElement>();

    const [language, setLanguage] = useState<{language: string; code: string}>(userAiSettings?.defaultLanguage ? {language: languages.find(item => item.code == userAiSettings?.defaultLanguage)?.language ?? '', code: userAiSettings?.defaultLanguage} : {language: 'English', code: 'en'});

    useEffect(() => {
      setLanguage(userAiSettings?.defaultLanguage ? {language: languages.find(item => item.code == userAiSettings?.defaultLanguage)?.language ?? '', code: userAiSettings?.defaultLanguage} : {language: 'English', code: 'en'});
    }, [userAiSettings?.defaultLanguage]);

    const onLanguageChangeHandler = async (value, option) => {
      if (option.value) {
        try {
          await updateUserAiSettings({language: option.value});
          setLanguage({language: option.country, code: option.value});
        } catch (err) {
          notification.error('', err);
        }
      }
    };

    const {Option} = Select;
    const selectSufixIcon = <FontAwesomeIcon icon={faCaretDown} color={'#121212'} />;

    useEffect(() => {
      function handleClickOutside(event) {
        if (popupRef.current && (!popupRef.current.contains(event.target) )) {
          setEditPageUrl(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [popupRef]);

    const getTopLinkUrl = () => {
      return content?.wpPublishedAtUrl || content?.importedFromUrl;
    };


    /*
      * Gets Id for wordpress page
      */
    const getTopLinkId = () => {
      if (content?.wpPublishedAtUrl) return wpArray.filter(item => getDomain(item.url) === getDomain(content?.wpPublishedAtUrl))[0]?.id;
      if (content?.importedFromUrl) return wpArray.filter(item => getDomain(item.url) === getDomain(content?.importedFromUrl))[0]?.id;
    };

    /*
  * Handles publishing of data from WordPress form
  */
    const publishWpPageHandler = async () => {
      const pageId = getTopLinkId();
      const pageUrl = getTopLinkUrl();

      const payload = {
        pageId: pageId,
        uuid: content?.uuid,
        pageUrl: pageUrl,
        postStatus: 'publish',
        ...((content?.wpPostCategories?.length && content?.wpPostType !== 'page') && {postCategories: content?.wpPostCategories}),
      };

      await publishWpPage(payload);
      await smallPageLoad(payload?.uuid);
    };

    const onEditUrlSubmit = (values: any) => {
      if (values.urlInput) {
        setKeepContentModal({
          show: true,
          url: values.urlInput,
          ...(values.renderWithbrowser && {renderWithbrowser: values.renderWithbrowser}),
        });
      }
    };

    const updateUrlHandler = async (shouldOverwriteContent: boolean, urlInput: string, renderWIthBrowser?: boolean) => {
      setEditPageUrl(false);
      await uploadFromUrl(!URL_PATTERN.test(urlInput.toLowerCase()) ? `http://${urlInput}` : urlInput, shouldOverwriteContent, renderWIthBrowser);

      MixPanel.track(SCA_PAGE_URL_UPDATE, {
        'pageId': content.uuid,
        'description': 'Page URL was updated',
      });
    };

    const renderForm = () => {
      return <Form
        form={form}
        onFinish={async values => {
          if (!isCloseClicked) {
            onEditUrlSubmit(values);
          }
        }}
        initialValues={{
          urlInput: modalUrl,
        }}>
        <Text style={{fontSize: '12px'}} type='secondary'>{'Page URL'}</Text>
        <Form.Item
          name='urlInput'
          rules={[
            {
              required: true,
              message: t('url-is-required'),
            },
            () => ({
              validator(_, value) {
                if (!value || isURL(value.trim(), {require_protocol: false})) {
                  return Promise.resolve();
                }
                return Promise.reject(t('url-not-valid'));
              },
            }),
          ]}
        >
          <CommonInput
            name='1'
            onChange={e => {
              setModalUrl(e.target.value);
              if (!e.target.value) {
                setSubmitDisabled(true);
              } else {
                setSubmitDisabled(false);
              }
            }
            }
            inputType='text'
            variant='light'
            placeholder={t('enter-url')}/>

        </Form.Item>
        <Form.Item name='renderWithbrowser' valuePropName='checked' style={{marginBottom: 0}}>
          <Checkbox className={styles.darkCheckbox}>Render page content with Browser</Checkbox>
        </Form.Item>

        <Form.Item className={styles.editUrlBtnContainer}>
          <Button
            buttonType='transparent-with-border'
            size='sm'
            className={styles.cancelImportUrlBtn}
            onClick={e => {
              e.preventDefault();
              setIsCloseClicked(true);
              setEditPageUrl(false);
              setModalUrl(content?.url || '');
            }}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? <Loader width={50}/> : t('cancel')}
          </Button>
          <Button
            buttonType='primary'
            type='submit'
            size='sm'
            className={styles.importUrlButton}
            disabled={submitDisabled}
            onClick={() => {
              setIsCloseClicked(false);
              setEditPageUrl(false);
            }}
          >
            {isAnalyzing ? <Loader width={50}/> : (content?.importedFromUrl ? 'Update & Import' : 'Add')}
          </Button>
        </Form.Item>
      </Form>;
    };


    // gets width of the text inside title input field and sets it as a width of the container
    // input field is 100% widht and will stretch to fill in the space
    // we set min width of 235px to adjust with current UI
    const calculateWidth = (() => {
      if (isNil(title)) {
        setTitleContainerWidth(`235px`);
        return;
      }
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const titleWidth = ctx.measureText(title);
      if (!titleWidth?.width) {
        setTitleContainerWidth(`235px`);
        return;
      }
      const formatedTitleWidth = titleWidth.width * 2.5 > 235 ? titleWidth.width * 2.5 : 235;
      setTitleContainerWidth(`${formatedTitleWidth}px`);
    });

    return (
      <>
        <WpInstructionsModal />
        <WpAddSiteModal />
        <CommonModal
          footer={null}
          visible={keepContentModal.show}
          onClose={() => setKeepContentModal({
            show: false,
            url: '',
            renderWithbrowser: false,
          })}
          style={{marginTop: 'auto', marginBottom: 'auto'}}>
          <EditUrlModalTitle>Update & Import</EditUrlModalTitle>
          <EditUrlModalSub>
                Do you want to overwrite the existing content in this article with new content from
            <EditUrlLink onClick={() => window.open(keepContentModal.url, '_blank')}>{keepContentModal.url}</EditUrlLink>?
          </EditUrlModalSub>
          <Divider />
          <EditUrlModalButtons>
            <GrayButton style={{marginLeft: 0}} onClick={() => {
              setKeepContentModal({
                show: false,
                url: '',
                renderWithbrowser: false,
              });
            }}>Cancel</GrayButton>
            <div style={{display: 'flex'}}>
              <GrayButton onClick={() => updateUrlHandler(false, keepContentModal.url, keepContentModal?.renderWithbrowser)}>No, keep existing content</GrayButton>
              <V2Button variant='ghost' style={{marginRight: 0}} onClick={() => updateUrlHandler(true, keepContentModal.url, keepContentModal?.renderWithbrowser)}>Yes, overwrite content</V2Button>
            </div>
          </EditUrlModalButtons>
        </CommonModal>
        <Modal
          footer={null}
          onCancel={() => setShowDownloadConfirmationModal(false)}
          visible={showDownloadConfirmationModal}>
          <div>{t('updating-url-data')}</div>
          <CheckboxStyled onChange={e => setRenderWithBrowser(e.target.checked)}>{t('render-with-browser')}</CheckboxStyled>
          <div style={{display: 'flex', marginTop: 20}}>
            {/* this closes the modal */}
            <GrayButton onClick={() => setShowDownloadConfirmationModal(false)}>{t('cancel')}</GrayButton>
            <V2Button onClick={() => {
              {/* we are passing true as a second param, which will tell the BE that we want to override the content.
                  very imporant, do not change to false*/}
              uploadFromUrl(content?.importedFromUrl || '', true, renderWithbrowser);
              setShowDownloadConfirmationModal(false);
            }}>{`Update & Import`}</V2Button>
          </div>

        </Modal>
        <Modal
          footer={null}
          onCancel={() => setShowQuickSyncModal(false)}
          visible={showQuickSyncModal}>
          <div>You are about to update the content of URL</div>
          <div style={{display: 'flex', marginTop: 20}}>
            <GrayButton onClick={() => setShowQuickSyncModal(false)}>{t('cancel')}</GrayButton>
            <V2Button onClick={() => {
              publishWpPageHandler();
              setShowQuickSyncModal(false);
            }}>{`Update`}</V2Button>
          </div>

        </Modal>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <div style={{display: 'flex', alignItems: 'center', width: titleContainerWidth}} ref={titleRef}>
            <Tooltip title={title ? 'Rename' : 'Please add an Article Title'} placement='bottom' overlayClassName={styles.copyTooltip}>

              <input
                className={classNames(styles.titleEditor, !title && styles.isError )}
                placeholder={placeholder === 'New document' ? t('new-document') : placeholder}
                value={title}
                defaultValue={content?.title || ''}
                onChange={isPublic ? () => { } : e => {
                  onChange(e);
                  debouncedChange(e);
                  calculateWidth();
                }}
                onBlur={isPublic ? () => { } : e => {
                  onChange(e);
                  debouncedChangeFocusOut(e);
                  calculateWidth();
                }}
                readOnly={isPublic}
              />
            </Tooltip>

          </div>
          <ScaArticleStatusSelectorV2 articleUuid={content.uuid} articleStatus={content.status} isMinified={true}/>
          <div style={{marginLeft: 10}}>
            <ProjectsSelector projectName={content.projectName} pageUuid={content.uuid} minifiedVersion={true} disabled={!!isScaPublicPage()}/>
          </div>

        </div>

        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flexStart'}}>
          <div className={styles.Url}
            style={ !content?.importedFromUrl ? {width: 'auto', color: '#4E5156', cursor: 'default', textDecoration: 'none'} : {} }
            onClick={() => {
              if (content?.importedFromUrl) {
                window.open(url, '_blank');
              }
            }}>
            {content?.importedFromUrl ? url : 'Page created from scratch '}
          </div>
          <div>
            {!newPage && <div className={styles.linkAndCopyContainer}>
              {!isPublic && <Dropdown visible={editPageUrl} overlay={<EditUrlContainer ref={popupRef}>{renderForm()}</EditUrlContainer>} placement={'bottomRight'} trigger={['click']}>
                <div style={{cursor: 'pointer', display: isExportingPage ? 'none' : 'block'}} onClick={() => setEditPageUrl(!editPageUrl)}>
                  <FontAwesomeIcon icon={faPen} className={styles.penIcon} />
                </div>
              </Dropdown>}
              {!isPublic &&
              <Tooltip title={content.importedFromUrl ? 'Download latest version of content from the import URL.': 'No import URL found'} placement='bottom' overlayClassName={styles.uploadDownloadPopup}>
                <FontAwesomeIcon color={content.importedFromUrl ? '#4e5156' : 'lightgray'} icon={faArrowDownToBracket} className={styles.downloadIcon} onClick={() => content.importedFromUrl ? setShowDownloadConfirmationModal(true) : {}}/>
              </Tooltip>}


              {!isPublic ?
                !postingData ? <Tooltip overlayClassName={styles.uploadDownloadPopup}
                  title={content.wpPublishedAtUrl ? 'Update the content on your WordPress page.' : 'This article is not published yet.'}
                  placement='bottom'>
                  <FontAwesomeIcon icon={faArrowUpFromBracket} className={styles.uploadIcon} onClick={() => {
                    if (!content.wpPublishedAtUrl) return;
                    setShowQuickSyncModal(true);
                  }} style={!content.wpPublishedAtUrl ? {opacity: '50%'} : {}}/>
                </Tooltip> : <Spin indicator={antIcon} /> : null}
              <MetaTagGenerator />
              {!isScaPublicPage() ? <OneClickPublisherV2 /> : null}
              <Tooltip placement='top' title={language.code === 'en' ? language.language : `AI Writer language changed to ${language.language}`} >
                <StyledSelect
                  variant='grey'
                  showSearch
                  value={language.language}
                  width={'140px'}
                  height={'22px'}
                  style={{marginLeft: '8px'}}
                  onChange={onLanguageChangeHandler}
                  suffixIcon={selectSufixIcon}
                  filterOption={(input, option) =>
                    option.country.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onMouseEnter={()=><Tooltip title={'Download latest version of content from the import URL.'} placement='bottom' ></Tooltip>}
                >
                  {
                    languages.map((lang, index) => {
                      return (
                        <Option disabled={false} key={index} country={lang.language} title={' '} value={lang.code}>
                          <Flag width={12} height={6} code={formatCountryCode(lang.language, lang.code)} style={{marginRight: '5px'}}></Flag>
                          {lang.language}
                        </Option>
                      );
                    })
                  }
                </StyledSelect>
              </Tooltip>
            </div>}

          </div>
        </div>
        {viewers > 1 && !newPage ?
          <div className={styles.peopleEditingContainer} style={{left: titleLeft ?? 0}}>
            <div style={{
              color: '#fff',
              padding: '3px 3px 3px 8px',
              borderRadius: 5,
              marginRight: 'auto',
              backgroundColor: 'black',
              display: 'flex',
              alignItems: 'center',
              fontSize: 12,
              width: collapsedViewers ? 43 : 285,
              overflow: 'hidden',
              transition: 'all .2s ease'}}>
              <NextImg style={{marginRight: '3px'}} src='/icons/spark.svg' />
              <span style={{marginRight: 5, marginLeft: 5}}>{viewers}</span> people currently editing this project
              <div
                style={{marginLeft: 10, marginRight: 10, display: 'flex', cursor: 'pointer'}}
                onClick={() => setCollapsedViewers(!collapsedViewers)}>
                <FontAwesomeIcon style={{marginTop: 'auto', marginBottom: 'auto', color: '#4e5156'}} icon={faXmark} fontSize={20}/>
              </div>
            </div>
          </div> : null}
      </>
    );
  },
);

const EditUrlContainer = styled.div`
  width: 380px;
  height: auto;
  background: #FFFFFF;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  padding: 15px 10px;
  
  .ant-form-item {
    margin-bottom: 0 !important;
    margin-top: 8px !important;
    input {
      height: 32px !important;
    }
  }
`;

const CheckboxStyled = styled(Checkbox)`
margin-top: 10px;
.ant-checkbox {
  .ant-checkbox-inner {
    border: 1px solid #7f4ead;
    border-radius: 5px;
  }

  &:hover {
    .ant-checkbox-inner {
      border: 1px solid #7f4ead;
    }
  }

  &.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #7f4ead;
    }

    &::after {
      border: 1px solid #7f4ead;
    }
  }
}

`;
const EditUrlModalTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  margin-bottom: 10px;
`;
const EditUrlModalSub = styled.div`
  color: #4E5156;
`;

const EditUrlLink = styled.div`
  color: #2e6cca;
  cursor: pointer;
  display: inline-block;
  margin-left: 5px;

  &:hover {
    text-decoration: underline;
  }
`;

const EditUrlModalButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const dropdownColors = {
  grey: {
    color: '#333',
    backgroundColor: '#F9F9FB',
    listItemsBackgroundColor: '#F9F9FB',
    borderColor: '#F9F9FB',
  },
};


const StyledSelect = styled(Select)<{variant?: string; borderRadius?: string; width?: string; height?: string; marginLeft?: string}>`
  color:${ p => dropdownColors[p.variant].color};
    background-color: ${ p => dropdownColors[p.variant].backgroundColor} !important;
    border-radius: ${ p => p.borderRadius ? p.borderRadius : '5px'} !important;
  .ant-select-selector {
    max-width:180px !important;
    min-width: ${p => p.width ? p.width : '168px'} !important;
    width: ${p => p.width ? p.width : '168px'} !important;
    height: ${p => p.height ? p.height : '38px'} !important;
    border-radius: ${ p => p.borderRadius ? p.borderRadius : '5px'} !important;
    background-color: ${ p => dropdownColors[p.variant].backgroundColor} !important;
    border-color: #E8E8E8 !important;
    padding: 1px 8px 2px 11px !important;
    cursor: pointer !important;
  }
  .ant-select-selection-search {
    padding: 0px !important;

    .ant-select-selection-search-input{
      height: 22px !important;
    }
  }

 .ant-select-arrow {
  color:${ p => dropdownColors[p.variant].color};
  }
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled){
    background-color: ${ p => dropdownColors[p.variant].backgroundColor} !important;
    border-color: ${ p => dropdownColors[p.variant].borderColor} !important
  }
  .ant-select-selection-item {
    display: flex;
    align-items: center;
  }

`;
